import {keyframes} from '@emotion/react';
import {Box, Portal, Typography} from '@mui/material';
import {Trans} from 'tolgee';

import {styled} from '~constants/theme';
import {getGlobalLoadingType} from '~redux/reducers/globalReducer';
import {useAppSelector} from '~redux/index';

import {useAnnotatorPortalRoot} from '~components/images/annotator/hooks/useAnnotatorPortalRoot';

const MaddoxAiLogo = styled('img')(({theme}) => ({
  margin: theme.spacing(4, 2.5, 5, 2.5),
}));

const gradientShift = keyframes({
  '0%': {
    backgroundPosition: '0% 50%',
  },
  '50%': {
    backgroundPosition: '100% 50%',
  },
  '100%': {
    backgroundPosition: '0% 50%',
  },
});

const AnimatedGradientBox = styled(Box)(({theme}) => ({
  height: 4,
  background: `linear-gradient(-45deg, ${theme.palette.info.light}, ${theme.palette.primary.light}, ${theme.palette.primary.main}, ${theme.palette.primary.light}, ${theme.palette.info.light})`,
  backgroundSize: '400% 400%',
  animation: `${gradientShift} 3s ease-in-out infinite`,
}));

interface GlobalLoadingIndicatorProps {
  show: boolean;
}

export function GlobalLoadingIndicator({show}: GlobalLoadingIndicatorProps) {
  const loadingType = useAppSelector(getGlobalLoadingType);
  const portalContainer = useAnnotatorPortalRoot();

  if (!show || typeof window === 'undefined' || !portalContainer) {
    return null;
  }
  // non-blocking
  if (loadingType === 'non-blocking') {
    if (!portalContainer) {
      return null;
    }

    return (
      <Portal container={portalContainer}>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
          }}
        >
          <AnimatedGradientBox />
        </Box>
      </Portal>
    );
  }

  // in dev mode document might not be defined
  if (!portalContainer) {
    return null;
  }

  // blocking
  return (
    <Portal container={portalContainer}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <AnimatedGradientBox />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              marginBottom: 2,
            }}
          >
            <MaddoxAiLogo src={'/gifs/maddox_logo_animated.gif'} alt='Maddox AI' />
          </Box>
          <Typography
            variant='body1'
            sx={{
              fontSize: '16px',
              textAlign: 'center',
            }}
          >
            <Trans keyName='operationInProgress' ns='common' />
          </Typography>
        </Box>
      </Box>
    </Portal>
  );
}
